/**
 * df-search.js — compréhension de la requête client avant d'interroger Shopify.
 *
 * POURQUOI. Chez nous le modèle de téléphone est une OPTION DE VARIANTE (« Mobile »,
 * jusqu'à 31 valeurs par produit), pas un mot du titre. Taper « iphone 15 pro max »
 * fait donc correspondre presque tout le catalogue : la recherche native renvoie
 * 1000 résultats. Plus le client précise, pire c'est.
 *
 * DEUX TECHNIQUES, exclusives l'une de l'autre (contrainte Shopify mesurée le 2026-09-06 :
 * dès qu'un paramètre `filter.*` est présent, la syntaxe `title:` ET l'opérateur `AND`
 * sont IGNORÉS — « title:laia » + filtre Chaine renvoyait les 503 chaînes) :
 *
 *   FILTRES + mots simples. Le classement par pertinence de Shopify et les boosts de
 *   Search & Discovery restent actifs. C'est la voie par défaut :
 *     B.  un modèle est reconnu     ->  filter.v.option.mobile (+ product_type), q = mots restants
 *         « coque iphone 15 pro max » : 1000 -> 280
 *     A.  aucun modèle, 0 ou 1 mot libre  ->  filter.p.product_type si le type est reconnu, q = le mot
 *         « chaine courte » : 480 -> 202 ; « perles » : 305, chaînes à perles en tête
 *
 *   SYNTAXE `title:` / `body:` avec un vrai ET. Précise, mais Shopify ne classe plus rien
 *   (ordre arbitraire) et ignore les boosts (WEBLCFB2C-214, mesuré le 2026-10-02). Réservée à :
 *     A.  aucun modèle, 2 mots libres ou plus  ->  q = title:type AND (title:mot OR body:mot) …
 *         « chaine longue laia » : 568 -> 5
 *
 * La carte des modèles vient de snippets/df-search-map.liquid, généré depuis le
 * catalogue vivant par scripts/build-search-map.mjs. Les valeurs sont envoyées
 * VERBATIM : on ne « corrige » jamais la donnée source ici.
 */
(function () {
  'use strict';

  var mapEl = document.getElementById('df-search-map');
  if (!mapEl) return;

  var MAP;
  try {
    MAP = JSON.parse(mapEl.textContent);
  } catch (e) {
    return; // carte illisible : on ne touche à rien, la recherche native reprend la main
  }

  /* ------------------------------------------------------------------ */
  /* Normalisation — DOIT rester identique à normalize() du générateur.  */
  /* ------------------------------------------------------------------ */

  function normalize(s) {
    return String(s)
      .toLowerCase()
      .normalize('NFD').replace(/[̀-ͯ]/g, '')  // chaîne -> chaine
      .replace(/\+/g, ' plus ')                          // S10+ == « S10 plus », != S10
      .replace(/[^a-z0-9]+/g, ' ')
      .trim();
  }

  /* Expressions en DEUX mots, remplacées avant le découpage.
     Relevées dans les vraies recherches clients (90 jours) : « mag safe » 73,
     « i phone 14 » / « coque i phone 15 ». */
  var PHRASES = [
    [/\bmag safe\b/g, 'magsafe'],
    [/\bi phone\b/g, 'iphone']
  ];

  /* Mots vides : on les retire SEULEMENT s'il reste autre chose.
     « phone chain » (120 recherches) doit devenir « chaine », pas « phone chaine ». */
  var NOISE = { phone: 1, pour: 1, de: 1, du: 1, la: 1, le: 1, les: 1, un: 1, une: 1, avec: 1 };

  /* Fautes et raccourcis de saisie. Corriger la requête CLIENT est légitime ;
     corriger la donnée source ne l'est pas (voir search-map-anomalies.txt).
     Les traductions viennent des recherches réelles : « chain » 222, « case » 89,
     « short » 104, « pearl » 85, « mirror » 46, « gold » 43, « long » 34, « cord » 17.
     Chaque cible a été vérifiée présente dans les TITRES du catalogue. */
  var ALIAS = {
    aiphone: 'iphone', ihpone: 'iphone', ipohne: 'iphone', iphonne: 'iphone',
    ipone: 'iphone', iph: 'iphone', apple: 'iphone',
    // le catalogue dit « Samsung Galaxy S24 Ultra », le client tape « samsung s24 ultra »
    // ou « galaxy s24 ultra » : les deux doivent mener au même modèle.
    // prepare() dédoublonne, donc « samsung galaxy » ne devient jamais « samsung galaxy galaxy ».
    samsung: 'samsung galaxy', galaxy: 'samsung galaxy',
    samsun: 'samsung galaxy', samsng: 'samsung galaxy', sansung: 'samsung galaxy',
    coques: 'coque', etui: 'coque', etuis: 'coque', housse: 'coque', case: 'coque',
    cases: 'coque',
    chaines: 'chaine', chainette: 'chaine', cordons: 'cordon',
    chain: 'chaine', chains: 'chaine', cord: 'cordon',
    short: 'courte', long: 'longue', mirror: 'miroir',
    pearl: 'perle', pearls: 'perle', gold: 'dore',
    // ajoutés d'après les recherches réelles (90 j) — chaque cible vérifiée
    // présente dans le lexique du catalogue
    adapter: 'adaptateur', adapters: 'adaptateur',
    ring: 'anneaux', rings: 'anneaux',
    pink: 'rose', green: 'vert', doree: 'dore', dorée: 'dore',
    battery: 'batterie', leather: 'cuir', necklace: 'collier',
    card: 'carte', holder: 'support',
    vitre: 'vitres', verre: 'vitres', trempe: 'vitres',
    max: 'max', pm: 'pro max', ultra: 'ultra'
  };

  /* « iphone15 » -> « iphone 15 », « s24ultra » -> « s24 ultra ».
     On ne coupe QUE sur un mot d'au moins 3 lettres : « s24 » et « 16e » sont des
     codes de modèle, les casser ferait perdre la correspondance avec la carte. */
  function splitAlnum(n) {
    return n
      .replace(/([a-z]{3,})(\d)/g, '$1 $2')
      .replace(/(\d)([a-z]{3,})/g, '$1 $2')
      .replace(/(\d)\s*cm\b/g, '$1 cm')     // « 40cm » : unité collée, très fréquent
      .replace(/\s+/g, ' ')
      .trim();
  }

  /* ------------------------------------------------------------------ */
  /* Lexique du catalogue et résolution des mots inconnus                */
  /* ------------------------------------------------------------------ */

  /* MAP.l = { mot: fréquence } pour tous les mots des titres, des modèles et des
     types. Un mot ABSENT est soit une faute, soit une troncature, soit de l'anglais.

     POURQUOI ça compte : Shopify a une tolérance native aux fautes, mais elle est
     DÉSACTIVÉE dès qu'on utilise `title:`. Mesuré le 2026-09-08 : « bandama » ->
     23 résultats, « title:bandama » -> 0. Donc on ne scope sur le titre QUE si tous
     les mots existent vraiment. Sinon on laisse la recherche native travailler. */
  // LEX_TITLE : mots présents dans les TITRES. Eux seuls rendent `title:` sûr.
  // LEX_ALL   : + les mots des modèles et des types. Bons pour CORRIGER une faute,
  //             mais scoper dessus renvoie 0 — « samsung » ne vit dans aucun titre,
  //             il vient de l'option Mobile (mesuré : `title:samsung` -> 0 résultat).
  var LEX_TITLE = MAP.l || {};
  var LEX = {};
  (function () {
    var k;
    for (k in LEX_TITLE) LEX[k] = LEX_TITLE[k];
    var lv = MAP.lv || {};
    for (k in lv) LEX[k] = (LEX[k] || 0) + lv[k];
  })();

  /* Distance de Damerau-Levenshtein bornée : on sort dès qu'on dépasse `max`. */
  function editDistance(a, b, max) {
    if (Math.abs(a.length - b.length) > max) return max + 1;
    var prev = [], cur = [], i, j;
    for (j = 0; j <= b.length; j++) prev[j] = j;
    for (i = 1; i <= a.length; i++) {
      cur[0] = i;
      var best = cur[0];
      for (j = 1; j <= b.length; j++) {
        cur[j] = Math.min(
          prev[j] + 1,
          cur[j - 1] + 1,
          prev[j - 1] + (a.charAt(i - 1) === b.charAt(j - 1) ? 0 : 1)
        );
        if (cur[j] < best) best = cur[j];
      }
      if (best > max) return max + 1;
      for (j = 0; j <= b.length; j++) prev[j] = cur[j];
    }
    return prev[b.length];
  }

  /* Un candidat n'est retenu que s'il ÉCRASE le suivant (facteur 3). Sans cette
     règle, la correction par distance d'édition produit « case » -> « cafe »,
     « mirror » -> « marron », « pearl » -> « petra » : mesuré sur les vraies
     requêtes clients. Un mot douteux doit rester non résolu, pas être deviné. */
  var DOMINANCE = 3;

  function bestOf(cands) {
    if (!cands.length) return null;
    cands.sort(function (x, y) { return y[1] - x[1]; });
    if (cands.length === 1 || cands[0][1] >= DOMINANCE * cands[1][1]) return cands[0][0];
    return null;
  }

  /* Renvoie le mot à utiliser, ou null si on ne sait pas le résoudre. */
  function resolve(w) {
    if (LEX[w]) return w;                                   // connu tel quel
    if (w.length >= 4 && LEX[w.slice(0, -1)] && w.charAt(w.length - 1) === 's') {
      return w.slice(0, -1);                                // pluriel du client
    }
    if (LEX[w + 's']) return w + 's';                       // singulier du client
    var k, cands = [];
    if (w.length >= 4) {                                    // troncature : « adap » -> « adaptateur »
      for (k in LEX) {
        if (k.length > w.length && k.indexOf(w) === 0) cands.push([k, LEX[k]]);
      }
      var pref = bestOf(cands);
      if (pref) return pref;
    }
    if (w.length >= 6) {                                    // vraie faute : distance 1, mot long
      cands = [];
      for (k in LEX) {
        if (k.length >= 6 && k.charAt(0) === w.charAt(0) && editDistance(w, k, 1) === 1) {
          cands.push([k, LEX[k]]);
        }
      }
      var fix = bestOf(cands);
      if (fix) return fix;
    }
    return null;                                            // inconnu : on n'y touche pas
  }

  /* Résout une liste de mots. `known` n'est vrai que si CHAQUE mot résolu existe
     dans un TITRE — c'est la seule condition qui rende le scoping `title:` sûr. */
  function resolveAll(words) {
    var out = [], known = true;
    for (var i = 0; i < words.length; i++) {
      var r = resolve(words[i]);
      out.push(r || words[i]);
      if (!r || !LEX_TITLE[r]) known = false;
    }
    return { words: out, known: known };
  }

  function prepare(raw) {
    var n = normalize(raw);
    for (var i = 0; i < PHRASES.length; i++) n = n.replace(PHRASES[i][0], PHRASES[i][1]);
    n = splitAlnum(n);
    var out = [];
    n.split(' ').filter(Boolean).forEach(function (w) {
      // un alias peut produire plusieurs mots (« galaxy » -> « samsung galaxy »)
      (ALIAS[w] || w).split(' ').forEach(function (x) {
        // « verre trempe » alias tous les deux vers « vitres » : on ne garde qu'une fois
        if (x && out.indexOf(x) === -1) out.push(x);
      });
    });
    // les mots vides ne partent que s'il reste de la matière
    var kept = out.filter(function (w) { return !NOISE[w]; });
    return (kept.length ? kept : out).join(' ');
  }

  /* Retire une séquence de mots (et rend null si elle n'y est pas). */
  function removeSeq(words, seq) {
    var s = seq.split(' ');
    for (var i = 0; i + s.length <= words.length; i++) {
      var ok = true;
      for (var j = 0; j < s.length; j++) {
        if (words[i + j] !== s[j]) { ok = false; break; }
      }
      if (ok) return words.slice(0, i).concat(words.slice(i + s.length));
    }
    return null;
  }

  /* ------------------------------------------------------------------ */
  /* Analyse : modèle ? type ? mots restants ?                          */
  /* ------------------------------------------------------------------ */

  function parse(raw) {
    var words = prepare(raw).split(' ').filter(Boolean);
    var model = null, type = null, rest;

    // MAP.m est trié par forme normalisée la plus LONGUE d'abord :
    // « iphone 15 pro max » l'emporte sur « iphone 15 ».
    for (var i = 0; i < MAP.m.length; i++) {
      var after = removeSeq(words, MAP.m[i][0]);
      if (after) { model = { value: MAP.m[i][1], collection: MAP.m[i][2] }; words = after; break; }
    }
    for (var k = 0; k < MAP.t.length; k++) {
      var after2 = removeSeq(words, MAP.t[k][0]);
      if (after2) { type = { norm: MAP.t[k][0], value: MAP.t[k][1] }; words = after2; break; }
    }
    rest = words;
    return { model: model, type: type, rest: rest };
  }

  /* ------------------------------------------------------------------ */
  /* Construction de l'URL                                              */
  /* ------------------------------------------------------------------ */

  /* Chaque mot est cherché dans le titre OU la description (WEBLCFB2C-214 : « perles »
     n'est dans le titre que de 5 produits, dans la description de 278). Les `nType`
     premiers mots — ceux du TYPE (« coque », « chaine ») — restent sur le titre seul :
     « coque » est dans presque toutes les descriptions, `body:coque` renverrait 1000. */
  function titleQuery(words, nType) {
    return words.map(function (w, i) {
      return i < nType ? 'title:' + w : '(title:' + w + ' OR body:' + w + ')';
    }).join(' AND ');
  }

  /**
   * rung = cran de l'échelle de repli (voir relaxOnEmpty plus bas).
   *   A1 : titre + AND        A2 : requête brute
   *   B1 : modèle + type      B2 : modèle seul      B3 : modèle + type, mots abandonnés
   */
  function build(raw, rung) {
    // Shopify supplies the active language/market root, including its trailing slash.
    var root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    root = root.replace(/\/?$/, '/');
    var p = parse(raw);
    var q = new URLSearchParams();
    var plain = prepare(raw);
    // Cran non précisé : le MODE décide du point de départ de l'échelle.
    if (!rung) rung = p.model ? 'B1' : 'A1';

    if (p.model && rung !== 'A2') {
      // ---- MODE B : le modèle devient un FILTRE, jamais un mot-clé ----
      var restB = p.rest.slice();
      var applyType = p.type && (rung === 'B1' || rung === 'B3');
      // Dernier cran : les mots libres ne donnent rien. Plutôt que de retomber sur la
      // requête brute (1000 résultats sans rapport), on ABANDONNE les mots introuvables
      // et on garde le MODÈLE — le signal le plus sûr de la requête.
      // « coque licorne iphone 11 » -> les coques pour iPhone 11.
      if (rung === 'B3') restB = [];

      if (!restB.length && !applyType && p.model.collection) {
        return { url: root + 'collections/' + p.model.collection, rung: rung, mode: 'collection' };
      }
      q.set('q', restB.length ? restB.join(' ') : (p.type ? p.type.norm : plain));
      q.append('filter.v.option.mobile', p.model.value);
      if (applyType) q.append('filter.p.product_type', p.type.value);
    } else if (rung === 'A1' && !p.model && p.rest.length < 2 && (p.rest.length || p.type)) {
      // ---- MODE A, 0 ou 1 mot libre : le TYPE devient un filtre, le mot reste simple ----
      // Aucune syntaxe : Shopify classe par pertinence et applique les boosts de
      // Search & Discovery (WEBLCFB2C-214 : avec `title:`/`body:` l'ordre était arbitraire —
      // « perles » ouvrait sur dix bijoux de sac — et les boosts du merch étaient ignorés).
      q.set('q', p.rest.length ? p.rest[0] : p.type.norm);
      if (p.type) q.append('filter.p.product_type', p.type.value);
    } else if (rung === 'A1' && !p.model && p.rest.length) {
      // ---- MODE A, 2 mots libres ou plus : TITRE (+ description), avec un vrai ET ----
      // En mots simples Shopify fait un OU : « chaine longue laia » 5 -> 269. On garde
      // la syntaxe, au prix du classement et des boosts.
      // Le scoping `title:` coupe la tolérance native de Shopify : on ne se
      // l'autorise que si CHAQUE mot existe dans le catalogue (après correction
      // des pluriels et des troncatures). Sinon -> requête simple, tolérance intacte.
      var typeWords = p.type ? p.type.norm.split(' ') : [];
      var rA = resolveAll(typeWords.concat(p.rest));
      if (rA.known) {
        q.set('q', titleQuery(rA.words, typeWords.length));
      } else {
        q.set('q', rA.words.join(' '));
      }
    } else {
      // ---- Dernier cran : la requête telle que le client l'a tapée ----
      q.set('q', raw);
    }

    q.set('lcf_q', raw);   // requête humaine, ré-affichée dans le champ
    q.set('lcf', rung);    // cran atteint — borne l'échelle, interdit toute boucle
    return { url: root + 'search?' + q.toString(), rung: rung, mode: p.model ? 'B' : 'A' };
  }

  /* ------------------------------------------------------------------ */
  /* Interception de la soumission                                      */
  /* ------------------------------------------------------------------ */

  function onSubmit(event) {
    var form = event.target;
    if (!form || form.tagName !== 'FORM') return;
    var input = form.querySelector('input[name="q"]');
    if (!input) return;
    var raw = (input.value || '').trim();
    if (!raw) return;
    // Un formulaire qui porte déjà des filtres (facettes) n'est pas réécrit.
    if (form.querySelector('[name^="filter."]')) return;

    event.preventDefault();
    window.location.href = build(raw).url;
  }

  document.addEventListener('submit', onSubmit, true);

  /* ------------------------------------------------------------------ */
  /* Sur la page de résultats                                           */
  /* ------------------------------------------------------------------ */

  function onSearchPage() {
    var meta = document.getElementById('df-search-meta');
    if (!meta) return;

    var params = new URLSearchParams(window.location.search);
    var human = params.get('lcf_q');
    var rung = params.get('lcf');
    var count = parseInt(meta.getAttribute('data-count'), 10);

    // 1. Ré-afficher la requête HUMAINE, jamais « title:chaine AND title:laia ».
    if (human) {
      Array.prototype.forEach.call(document.querySelectorAll('input[name="q"]'), function (el) {
        // sauf les champs cachés des formulaires de facettes, traités juste après
        if (el.type !== 'hidden') el.value = human;
      });
      // « 7 résultats pour "title:chaine AND …" » -> « … pour "chaine longue laia" »
      // Vaut aussi pour le <title> : il s'affiche dans l'onglet et dans les partages.
      var sent = meta.getAttribute('data-terms');
      if (sent) {
        Array.prototype.forEach.call(document.querySelectorAll('[data-df-count-label]'), function (el) {
          if (el.textContent.indexOf(sent) !== -1) {
            el.textContent = el.textContent.split(sent).join(human);
          }
        });
        if (document.title.indexOf(sent) !== -1) {
          document.title = document.title.split(sent).join(human);
        }
      }
    }

    // 2. Désamorcer le piège des facettes : cliquer un filtre depuis le mode A
    //    ré-enverrait « title:… » AVEC un filtre, que Shopify ignore -> explosion.
    //    On fait porter aux formulaires de facettes la requête SIMPLE.
    if (human) {
      Array.prototype.forEach.call(document.querySelectorAll('form'), function (form) {
        if (!form.querySelector('[name^="filter."]')) return;
        var hidden = form.querySelector('input[type="hidden"][name="q"]');
        if (hidden) hidden.value = prepare(human);
        var keep = form.querySelector('input[type="hidden"][name="lcf_q"]');
        if (!keep) {
          keep = document.createElement('input');
          keep.type = 'hidden'; keep.name = 'lcf_q'; keep.value = human;
          form.appendChild(keep);
        }
      });
    }

    // 3. Échelle de repli : zéro résultat -> on relâche d'UN cran, bornés.
    if (human && rung && count === 0) {
      var next = { A1: 'A2', B1: 'B2', B2: 'B3' }[rung];
      if (next) {
        window.location.replace(build(human, next).url);
      }
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', onSearchPage);
  } else {
    onSearchPage();
  }

  // exposé pour la vérification en console / les tests
  window.LCFSearch = { parse: parse, build: build, normalize: normalize, prepare: prepare,
                       resolve: resolve, resolveAll: resolveAll, lexicon: LEX, lexiconTitle: LEX_TITLE };
})();
